import React from "react";
import { motion } from "framer-motion";
import {
    Search,
    MapPin,
    Stethoscope,
    Video,
    Building2,
    CalendarCheck,
    ShieldCheck,
    Star,
    ArrowRight,
    Check,
    Clock3,
    UserRound,
    FlaskConical,
    HeartPulse,
    FileText
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import findcarehero from "../../../assets/images/findcare.png"
import { Link } from "react-router-dom";

const FindCare = () => {
    const careOptions = [
        {
            icon: Stethoscope,
            title: "Find a Doctor",
            description:
                "Discover healthcare professionals based on your care needs and available services.",
        },
        {
            icon: Video,
            title: "Virtual Care",
            description:
                "Connect with healthcare professionals through supported virtual consultation services.",
        },
        {
            icon: Building2,
            title: "In-Clinic Care",
            description:
                "Find participating clinics and healthcare providers for in-person care.",
        },
        {
            icon: FlaskConical,
            title: "Diagnostics",
            description:
                "Discover supported diagnostic and laboratory services as part of your care journey.",
        },
    ];

    const steps = [
        {
            number: "01",
            icon: Search,
            title: "Search",
            description:
                "Tell us what type of care or healthcare professional you are looking for.",
        },
        {
            number: "02",
            icon: UserRound,
            title: "Choose",
            description:
                "Review available providers and choose the option that fits your needs.",
        },
        {
            number: "03",
            icon: CalendarCheck,
            title: "Book",
            description:
                "Select an available appointment or supported consultation option.",
        },
        {
            number: "04",
            icon: HeartPulse,
            title: "Get Care",
            description:
                "Continue your healthcare journey through Doxzon's connected workflows.",
        },
    ];

    const benefits = [
        "Discover relevant healthcare providers",
        "Search for different types of care",
        "Compare available care options",
        "Choose virtual or in-clinic care where supported",
        "Book appointments through connected workflows",
        "Continue care with connected health information",
    ];

    const providerTypes = [
        "General Physicians",
        "Specialists",
        "Clinics",
        "Diagnostic Centers",
        "Healthcare Services",
        "Virtual Care Providers",
    ];

    const faqs = [
        {
            question: "What can I find through Doxzon Find Care?",
            answer:
                "Find Care helps patients discover supported healthcare professionals, clinics and healthcare services based on their care needs.",
        },
        {
            question: "Can I find a doctor near me?",
            answer:
                "Where location-based provider discovery is supported, you can search for available healthcare providers and services based on location.",
        },
        {
            question: "Can I book an appointment after finding a doctor?",
            answer:
                "Yes. Where appointment scheduling is available, patients can continue from provider discovery to the supported booking workflow.",
        },
        {
            question: "Can I choose between virtual and in-clinic care?",
            answer:
                "Where supported by the provider, patients may be able to choose between virtual consultation and in-clinic care.",
        },
        {
            question: "Can I find diagnostic services?",
            answer:
                "Supported diagnostic and laboratory services can be discovered as part of the connected Doxzon healthcare journey.",
        },
    ];

    return (
        <>
            <Navbar />

            <main className="bg-white text-slate-900 overflow-hidden">

                {/* HERO */}
                <section className="relative overflow-hidden bg-[#f4fbfc]">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-teal-200/30 blur-3xl" />
                        <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />
                        <div
                            className="absolute inset-0 opacity-[0.025]"
                            style={{
                                backgroundImage:
                                    "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                                backgroundSize: "48px 48px",
                            }}
                        />
                    </div>

                    <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-[1.05fr_.95fr] gap-14 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.75 }}
                        >
                            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-3 py-1.5 shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-700">
                                    Find Care
                                </span>
                            </div>

                            <h1 className="font-sora text-4xl md:text-5xl lg:text-[50px] font-medium tracking-[-0.05em] leading-[1.08] mt-6 max-w-2xl">
                                Find the right care,
                                <span className="block text-teal-500 mt-2">
                                    when you need it.
                                </span>
                            </h1>

                            <p className="text-slate-600 text-base md:text-lg leading-7 mt-6 max-w-xl">
                                Discover healthcare professionals, clinics and supported
                                healthcare services through one connected Doxzon experience.
                            </p>

                            <div className="mt-7 max-w-xl  p-1.5 ">
                                <div className="grid md:grid-cols-[1fr_160px_auto] gap-1.5">
                                    {/* <div className="flex items-center gap-2.5 px-3.5 h-11 rounded-lg bg-slate-50">
                                        <Search size={16} className="text-teal-600 shrink-0" />
                                        <input
                                            type="text"
                                            placeholder="Doctor, specialty or service"
                                            className="w-full bg-transparent outline-none text-xs text-slate-700 placeholder:text-slate-400"
                                        />
                                    </div>

                                    <div className="flex items-center gap-2.5 px-3.5 h-11 rounded-lg bg-slate-50">
                                        <MapPin size={15} className="text-teal-600 shrink-0" />
                                        <input
                                            type="text"
                                            placeholder="Location"
                                            className="w-full bg-transparent outline-none text-xs text-slate-700 placeholder:text-slate-400"
                                        />
                                    </div> */}

                                    <Link to="/consultdoctor">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className="h-11 px-5 rounded-lg bg-slate-950 text-white inline-flex items-center justify-center gap-2 text-xs font-semibold"
                                        >
                                            Find Care
                                            <ArrowRight size={14} />
                                        </motion.button>
                                    </Link>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 mt-3.5">
                                <span className="text-[11px] font-medium text-slate-400 mr-1">
                                    Popular:
                                </span>

                                {["General Physician", "Dentist", "Cardiologist", "Dermatologist"].map(
                                    (item) => (
                                        <button
                                            key={item}
                                            type="button"
                                            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:border-teal-300 hover:text-teal-600 transition"
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            </div>

                            <div className="flex flex-wrap gap-5 mt-6 text-xs text-slate-500">
                                <span className="inline-flex items-center gap-2">
                                    <ShieldCheck size={14} className="text-teal-500" />
                                    Connected healthcare
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <Check size={14} className="text-teal-500" />
                                    Multiple care options
                                </span>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 35, scale: 0.96 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            transition={{ duration: 0.9, delay: 0.1 }}
                            className="relative w-full max-w-2xl mx-auto lg:max-w-5xl xl:max-w-6xl"
                        >
                            <div className="absolute -inset-10 rounded-[45px] bg-teal-300/20 blur-3xl" />

                            <div className="relative w-full rounded-[28px] bg-white p-2 shadow-[0_30px_80px_rgba(15,23,42,0.18)] border border-white">
                                <img
                                    src={findcarehero}
                                    alt="Find healthcare providers and services with Doxzon"
                                    className="w-full h-[600px] md:h-[700px] lg:h-[780px] xl:h-[850px] rounded-[22px] object-cover"
                                />
                            </div>

                            {/* Connected Care */}
                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{
                                    duration: 3.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className="absolute -left-6 bottom-10 lg:-left-10 rounded-xl bg-white border border-slate-100 shadow-xl px-4 py-3"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-teal-50 grid place-items-center">
                                        <HeartPulse size={18} className="text-teal-600" />
                                    </div>

                                    <div>
                                        <p className="text-xs font-bold text-slate-800">
                                            Connected Care
                                        </p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            From search to follow-up
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Care Options */}
                            <motion.div
                                animate={{ y: [0, 8, 0] }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className="absolute -right-6 top-10 lg:-right-10 rounded-xl bg-slate-950 text-white shadow-xl px-4 py-3"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-teal-500/15 grid place-items-center">
                                        <MapPin size={16} className="text-teal-400" />
                                    </div>

                                    <div>
                                        <p className="text-xs font-bold">
                                            Care Options
                                        </p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            Virtual & in-clinic
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* CARE OPTIONS */}
                <section className="py-20 md:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="max-w-2xl">
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-600">
                                Care Options
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
                                Start with the care
                                <span className="text-teal-500"> you need.</span>
                            </h2>
                            <p className="text-slate-600 text-sm leading-6 mt-4">
                                Explore different healthcare options and continue your journey
                                through Doxzon's connected workflows.
                            </p>
                        </div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10"
                        >
                            {careOptions.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <motion.div
                                        key={item.title}
                                        initial={{ opacity: 0, y: 22 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.55, delay: index * 0.07 }}
                                        whileHover={{ y: -6 }}
                                        className="group rounded-2xl border border-slate-100 bg-slate-50/70 p-5 hover:bg-white hover:shadow-lg hover:border-teal-100 transition"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
                                            <Icon size={18} className="text-teal-600 group-hover:text-white transition" />
                                        </div>
                                        <h3 className="font-sora text-base font-semibold mt-4">{item.title}</h3>
                                        <p className="text-slate-500 text-xs leading-6 mt-2">{item.description}</p>
                                        <div className="inline-flex items-center gap-1.5 text-teal-600 text-xs font-semibold mt-4">
                                            {/* Explore <ArrowRight size={13} /> */}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </div>
                </section>

                {/* HOW IT WORKS */}
                <section className="py-20 md:py-24 bg-[#f6fbfc]">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-600">
                                How It Works
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                                Four steps to
                                <span className="text-teal-500"> better care.</span>
                            </h2>
                        </div>

                        <div className="relative grid md:grid-cols-4 gap-5 mt-12">
                            <div className="hidden md:block absolute top-6 left-[11%] right-[11%] h-px bg-teal-100" />

                            {steps.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <motion.div
                                        key={item.number}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5 }}
                                        className="relative text-center"
                                    >
                                        <div className="relative z-10 mx-auto w-12 h-12 rounded-xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">
                                            <Icon size={18} className="text-teal-400" />
                                        </div>
                                        <span className="block mt-4 text-[9px] font-bold tracking-[0.2em] text-teal-600">
                                            STEP {item.number}
                                        </span>
                                        <h3 className="font-sora text-base font-semibold mt-2">{item.title}</h3>
                                        <p className="text-slate-500 text-xs leading-5 mt-2 max-w-[210px] mx-auto">
                                            {item.description}
                                        </p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* PROVIDER TYPES */}
                <section className="py-20 md:py-24 bg-slate-950 text-white">
                    <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-400">
                                Healthcare Providers
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                                Find care from the
                                <span className="text-teal-400"> right provider.</span>
                            </h2>
                            <p className="text-slate-400 text-sm leading-7 mt-5 max-w-xl">
                                Discover supported healthcare professionals, clinics and
                                services based on the type of care you are looking for.
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-2.5">
                            {providerTypes.map((item, index) => (
                                <motion.div
                                    key={item}
                                    initial={{ opacity: 0, x: 15 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.06 }}
                                    whileHover={{ x: 5 }}
                                    className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] p-3"
                                >
                                    <div className="w-7 h-7 rounded-lg bg-teal-500/10 grid place-items-center shrink-0">
                                        <Stethoscope size={14} className="text-teal-400" />
                                    </div>
                                    <span className="text-xs text-slate-300 font-medium">{item}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PATIENT BENEFITS */}
                <section className="py-20 md:py-24">
                    <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-600">
                                Patient Benefits
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                                Less searching.
                                <span className="block text-teal-500">More connected care.</span>
                            </h2>
                            <p className="text-slate-600 text-sm leading-7 mt-5 max-w-xl">
                                Doxzon brings provider discovery, appointments and supported
                                healthcare services into one patient experience.
                            </p>
                        </div>

                        <div className="space-y-2.5">
                            {benefits.map((item, index) => (
                                <motion.div
                                    key={item}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ x: 5 }}
                                    className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm"
                                >
                                    <span className="w-7 h-7 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">
                                        <Check size={13} />
                                    </span>
                                    <span className="text-xs md:text-sm font-medium text-slate-700">{item}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CONNECTED CARE */}
                <section className="py-20 md:py-24 bg-gradient-to-br from-cyan-50 to-blue-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto">
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-600">
                                Connected Care
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                                Finding care is only
                                <span className="text-teal-500"> the beginning.</span>
                            </h2>
                            <p className="text-slate-600 text-sm leading-7 mt-4">
                                Once you find the right care, Doxzon can connect the next
                                steps of the healthcare journey where those services are supported.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-4 gap-3.5 mt-10">
                            {[
                                [Search, "Find Care"],
                                [CalendarCheck, "Appointment"],
                                [HeartPulse, "Consultation"],
                                [FileText, "Health Records"],
                            ].map(([Icon, label], index) => (
                                <motion.div
                                    key={label}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.08 }}
                                    whileHover={{ y: -5 }}
                                    className="relative rounded-xl bg-white border border-white p-4 text-center shadow-sm"
                                >
                                    <div className="w-10 h-10 mx-auto rounded-lg bg-teal-50 grid place-items-center">
                                        <Icon size={18} className="text-teal-600" />
                                    </div>
                                    <p className="text-sm font-semibold mt-3">{label}</p>
                                    {index < 3 && (
                                        <ArrowRight
                                            size={14}
                                            className="hidden md:block absolute -right-2.5 top-1/2 -translate-y-1/2 text-teal-300 bg-white"
                                        />
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="py-20 md:py-24 bg-white">
                    <div className="max-w-4xl mx-auto px-6">
                        <div className="text-center">
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-600">
                                FAQ
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                                Frequently asked questions
                            </h2>
                        </div>

                        <div className="space-y-2.5 mt-10">
                            {faqs.map((faq, index) => (
                                <motion.details
                                    key={faq.question}
                                    initial={{ opacity: 0, y: 15 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                    className="group rounded-xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                                >
                                    <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-5 font-sora text-sm font-semibold">
                                        <span>{faq.question}</span>
                                        <span className="w-7 h-7 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">
                                            <ArrowRight
                                                size={13}
                                                className="group-open:rotate-90 transition-transform"
                                            />
                                        </span>
                                    </summary>
                                    <p className="px-5 pb-5 text-slate-600 leading-6 text-xs md:text-sm">
                                        {faq.answer}
                                    </p>
                                </motion.details>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="px-6 pb-20">
                    <div className="relative overflow-hidden max-w-7xl mx-auto rounded-3xl bg-slate-950 text-white px-8 py-12 md:px-14 text-center">
                        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />
                        <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

                        <div className="relative">
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-teal-400">
                                Start Your Care Journey
                            </span>
                            <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                                Find care that works
                                <span className="text-teal-400"> for you.</span>
                            </h2>
                            <p className="text-slate-300 text-sm max-w-2xl mx-auto mt-4 leading-6">
                                Discover supported healthcare professionals, clinics and
                                services through Doxzon.
                            </p>
                            <Link
                                to="/consultdoctor"
                                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-semibold hover:bg-teal-50 transition"
                            >
                                Find Care
                                <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                </section>

            </main>
        </>
    );
};

export default FindCare;